// An <iframe> pointed at a blob the browser can't render doesn't show an empty
// frame — it triggers a download. That's what made "view this unmatched bill"
// silently download a Word invoice instead of showing it. Check before framing.

const RENDERABLE_PREFIXES = ['image/', 'text/'];

export const isBrowserRenderable = (mimeType: string | null | undefined): boolean => {
  const m = (mimeType ?? '').toLowerCase();
  if (!m) return false;
  if (m === 'application/pdf') return true;
  return RENDERABLE_PREFIXES.some((prefix) => m.startsWith(prefix));
};

// The inbox knows an attachment's MIME type because it fetches the bytes. The
// bill/site viewers only hold a storage path, and framing a stored Word or Excel
// file downloads it just the same (job 18's defect on a different screen). The
// extension is all we have there, and it's enough.
const RENDERABLE_EXTENSIONS = /\.(pdf|png|jpe?g|gif|webp|svg|txt|csv)$/i;

/**
 * Whether a stored file can be framed, judged by its filename. Unknown or
 * extension-less names are treated as renderable: the overwhelming majority are
 * PDFs, and the iframe is the intended experience — only the types known to
 * trigger a download are diverted.
 */
export const isRenderableFilename = (nameOrPath: string | null | undefined): boolean => {
  const name = (nameOrPath ?? '').split(/[?#]/)[0];
  const ext = /\.[a-z0-9]+$/i.exec(name)?.[0];
  if (!ext) return true;
  return RENDERABLE_EXTENSIONS.test(name);
};

/** Human label for a MIME type, for the "can't preview this" panel. */
export const describeAttachmentType = (mimeType: string | null | undefined): string => {
  const m = (mimeType ?? '').toLowerCase();
  if (m.includes('wordprocessingml') || m === 'application/msword') return 'Word document';
  if (m === 'application/pdf') return 'PDF';
  if (m.startsWith('image/')) return 'Image';
  if (!m || m.includes('octet-stream')) return 'File';
  return m;
};
